import { Button, Space, Tag, Tooltip } from 'antd';
import {
  AuditOutlined,
  CheckCircleTwoTone,
  CheckOutlined,
  DashOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MinusCircleOutlined,
  MinusOutlined
  // RetweetOutlined
} from '@ant-design/icons';
import { DICCIONARIO_COLOR_STATUS_INCOMES } from '../../utils/constants';

const ActiveColumn = {
  title: 'Activo',
  dataIndex: 'clie_activo',
  key: 'clie_activo',
  align: 'center',
  width: 150,
  render: (_, record) => {
    return record.clie_activo === 1 ? (
      <CheckCircleTwoTone twoToneColor="#389e0d" style={{ fontSize: '24px' }} />
    ) : (
      <MinusCircleOutlined style={{ fontSize: '20px', color: '#595959' }} />
    );
  }
};

/**
 * Create a column for antd table that renders a button to perform a custom action.
 * The button can be conditionally disabled based on the record data.
 *
 * @param {function} handleCustomAction - The function that will be called when the button is clicked.
 * @param {number} [width=150] - The width of the Actions column. Default is 150px.
 
 *
 * @returns {Object} A column definition for antd table.
 */

const ActionColumn = ({ handleCustomAction, width = 150, loader = false }) => ({
  title: 'Archivos',
  dataIndex: 'operation',
  key: 'operation',
  align: 'center',
  fixed: 'right',
  width,
  className: 'actions-column',
  render: (_, record) => {
    return (
      <Space size="middle">
        <Button
          disabled={!record.estado.includes('Facturado') || loader[record.oper_id + 'xml']}
          loading={loader[record.oper_id + 'xml']}
          onClick={() => (loader[record.oper_id + 'xml'] ? null : handleCustomAction(record, 'xml'))}>
          {loader[record.oper_id + 'xml'] ? '' : 'xml'}
        </Button>
        <Button
          danger
          disabled={!record.estado.includes('Facturado') || loader[record.oper_id + 'pdf']}
          loading={loader[record.oper_id + 'pdf']}
          onClick={() => (loader[record.oper_id + 'pdf'] ? null : handleCustomAction(record, 'pdf'))}>
          {loader[record.oper_id + 'pdf'] ? '' : 'pdf'}
        </Button>
      </Space>
    );
  }
});
const EmailColumn = ({ handleCustomAction, width = 150, loader = false }) => ({
  title: 'Email',
  dataIndex: 'email',
  key: 'email',
  align: 'center',
  fixed: 'right',
  width,
  className: 'actions-column',
  render: (_, record) => {
    return (
      <Space size="middle">
        <Button
          disabled={!record.estado.includes('Facturado') || loader[record.oper_id]}
          loading={loader[record.oper_id]}
          onClick={() => handleCustomAction(record)}>
          {loader[record.oper_id] ? '' : <MailOutlined />}
        </Button>
      </Space>
    );
  }
});

const RestampInvoiceColumn = ({ handleCustomAction, width = 150, loader = false }) => {
  return {
    title: 'Retimbrar',
    dataIndex: 'restamp',
    key: 'restamp',
    align: 'center',
    fixed: 'right',
    width,
    className: 'actions-column',
    render: (_, record) => {
      return (
        <>
          <Tooltip
            title={
              record.errtim_descripcion
                ? record.errtim_descripcion.includes('exitoso')
                  ? null
                  : record.errtim_descripcion
                : null
            }>
            <Space size="middle">
              <Button
                disabled={record.estado.includes('Facturado') || loader[record.oper_id]}
                loading={loader[record.oper_id]}
                onClick={() => (loader[record.oper_id] ? null : handleCustomAction(record))}>
                {loader[record.oper_id] ? '' : <AuditOutlined />}
              </Button>
            </Space>
          </Tooltip>
        </>
      );
    }
  };
};

const ActionColumnFact = (handleCustomAction, loadingInvoice) => ({
  title: 'Acciones',
  dataIndex: 'operation',
  key: 'operation',
  align: 'center',
  fixed: 'right',
  width: 150,
  className: 'actions-column',
  render: (_, record) => {
    return (
      <Space size="middle">
        <Button disabled={loadingInvoice} loading={loadingInvoice} onClick={() => handleCustomAction(record)}>
          Facturar
        </Button>
      </Space>
    );
  }
});

const StatusColumn = () => {
  return {
    title: 'Estado',
    dataIndex: 'status',
    key: 'status',
    align: 'center',
    sortDirections: ['ascend', 'descend'],
    sorter: (a, b) => a.status - b.status,
    render: (_, record) => {
      return record.status in DICCIONARIO_COLOR_STATUS_INCOMES ? (
        <>
          <Tooltip
            title={
              record.errtim_descripcion
                ? record.errtim_descripcion.includes('exitoso')
                  ? null
                  : record.errtim_descripcion
                : null
            }>
            <Tag style={{ cursor: 'pointer' }} color={record.estado_color}>
              <Space size="small">
                {record.estado}
                {record.errtim_descripcion && !record?.errtim_descripcion?.includes('exitoso') && (
                  <InfoCircleOutlined />
                )}
              </Space>
            </Tag>
          </Tooltip>
          {/* {record.errtim_descripcion && !record?.errtim_descripcion?.includes('exitoso') && (
            <Tooltip title="Reintentar">
              <Button
                variant="default"
                size="small"
                onClick={() => {
                  tryCorrelation(record);
                }}
                icon={<RetweetOutlined />}
                style={{ marginLeft: '7px' }}
              />
            </Tooltip>
          )} */}
        </>
      ) : (
        <Tag>
          <DashOutlined style={{ fontSize: '20px', height: '20px' }} />
        </Tag>
      );
    }
  };
};

const dateOperColumn = () => {
  return {
    title: 'Fecha de operación',
    dataIndex: 'oper_fecha',
    key: 'oper_fecha',
    align: 'center',
    sortDirections: ['ascend', 'descend'],
    sorter: (a, b) => new Date(a.oper_fecha) - new Date(b.oper_fecha)
  };
};

const InvoiceColumn = {
  title: 'Factura',
  dataIndex: 'isInvoice',
  key: 'isInvoice',
  align: 'center',
  sortDirections: ['ascend', 'descend'],
  sorter: (a, b) => a.isInvoice - b.isInvoice,
  render: (_, record) => {
    return record.isInvoice ? (
      <CheckOutlined style={{ fontSize: '20px', height: '20px', color: '#389e0d' }} />
    ) : (
      <MinusOutlined style={{ fontSize: '20px', height: '20px' }} />
    );
  }
};

export {
  ActiveColumn,
  ActionColumn,
  StatusColumn,
  dateOperColumn,
  InvoiceColumn,
  ActionColumnFact,
  EmailColumn,
  RestampInvoiceColumn
};
